import type { LayerKey } from '@festina-life/api'
import { typeUtils } from '@festina-life/core'
import { FormikFileInput, FormikForm } from '@festina-life/ui/formikForms'
import { useTranslate } from '@festina-life/ui/hooks'
import * as Yup from 'yup'

import type { ChangeOperation } from '../../uiModels/workspace/types'
import { useOperationsContext } from '../../workspace/operationsContext'
import { draftCreationStatusReporter } from '../draftCreationModal/draftCreationStatusReporter'
import type {
  DraftCreatedResult,
  DraftSubmitStatus,
} from '../draftCreationModal/types'
import type {
  OperationFileUploadEntityConfig,
  OperationFileUploadFileConfig,
} from './types'

type UploadData = {
  files: File[]
}

const validateKey = typeUtils.createValidator<UploadData>()

type Props<TOperation extends ChangeOperation> = {
  formId: string
  layer: LayerKey
  block: string
  fileConfig: OperationFileUploadFileConfig<TOperation>
  entityConfig: OperationFileUploadEntityConfig<TOperation>
  /** Turns the reason an upload failed into the message reported to the user. */
  getUploadErrorMessage: (error: string) => string
  expectedEntityName?: string
  onStatusChanged: (status: DraftSubmitStatus) => void
}

export const OperationFileUploadForm = <TOperation extends ChangeOperation>({
  formId,
  layer,
  block,
  fileConfig,
  entityConfig,
  getUploadErrorMessage,
  expectedEntityName,
  onStatusChanged,
}: Props<TOperation>) => {
  const translate = useTranslate()
  const { onAddOrUpdateOperation } = useOperationsContext()

  const initialValues: UploadData = {
    files: [],
  }

  const validationSchema: Yup.ObjectSchema<UploadData> = Yup.object({
    files: Yup.array(Yup.mixed<File>().required())
      .length(1, translate('validation.required'))
      .required(translate('validation.required'))
      .test('isExpectedFormat', fileConfig.invalidFileTypeError, (files) => {
        const file = files?.at(0)

        return (
          !file || file.name.toLowerCase().endsWith(fileConfig.fileExtension)
        )
      }),
  })

  const handleSubmit = async ({
    files,
  }: UploadData): Promise<DraftCreatedResult> => {
    // Validation only lets a single file through, so this merely narrows the type.
    const file = files.at(0)

    if (!file) {
      throw new Error(translate('validation.required'))
    }

    try {
      const createOperation = await fileConfig.convertToOperation(file)
      const entityId = createOperation
        ? entityConfig.getEntityId(createOperation)
        : undefined

      if (!createOperation || !entityId?.name) {
        throw new Error(entityConfig.missingEntityNameError)
      }

      if (expectedEntityName && entityId.name !== expectedEntityName) {
        throw new Error(
          entityConfig.getEntityNameMismatchError(
            entityId.name,
            expectedEntityName,
          ),
        )
      }

      const createdOnWorkspaceKey = await onAddOrUpdateOperation({
        operation: createOperation,
        layer,
        block,
        shouldOpenUnifiedBranchWorkspace: true,
      })

      return {
        createdDraft: entityId,
        addedToWorkspaceKey: createdOnWorkspaceKey,
      }
    } catch (error) {
      // Rethrown with the upload context so that whoever shows the error does not
      // have to know which kind of entity was being uploaded.
      throw new Error(
        getUploadErrorMessage(
          typeUtils.isError(error)
            ? error.message
            : translate('operationFileUploadForm.unknownError'),
        ),
        { cause: error },
      )
    }
  }

  return (
    <FormikForm
      formId={formId}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={draftCreationStatusReporter(onStatusChanged, handleSubmit)}
    >
      <FormikFileInput
        name={validateKey('files')}
        label={fileConfig.label}
        description={fileConfig.description}
        accept={fileConfig.fileExtension}
        multiple={false}
      />
    </FormikForm>
  )
}
